// ConfirmDiscard — the close-a-dirty-tab confirmation CONTENT, pure. Composes
// Button + Text; the HOST owns the Dialog chrome (title, scrim, Esc/focus
// handling), exactly like ExportFlow is dialog content.
//
// Reached only through the vocabulary's guard: the host ran `closeTab`
// (Navigation/types.js) and it reported `unsaved` with the blocked tab — that
// `ResourceTab` is this component's input, so the message can name the exact
// resource whose changes are at stake. Two DISTINCT intents, never one
// boolean-parameterised callback: `onKeep` (the safe way out — the tab stays)
// and `onDiscard` (the host applies `closeTabDiscarding`). Closing never
// deletes the resource, and the copy says so — the loss is the unsaved
// changes, nothing else.
//
// Register: the destructive choice is a quiet `danger` Button, the safe choice
// a neutral `secondary`; no accent is spent (discarding is never the primary
// action of a view).

import { Trans } from "@lingui/react/macro";
import { Button } from "../Button/Button.js";
import type { ResourceTab } from "../Navigation/types.js";
import { Text } from "../Text/Text.js";
import "./ResourceTabs.css";

export type ConfirmDiscardProps = {
    // The blocked tab, as `closeTab` reported it (`CloseBlocked.tab`).
    readonly tab: ResourceTab;
    // Keep the tab (and its unsaved changes) open — cancel the close.
    readonly onKeep: () => void;
    // Close the tab, losing the unsaved changes — the host runs
    // `closeTabDiscarding` and dismisses the dialog.
    readonly onDiscard: () => void;
};

export const ConfirmDiscard = ({
    tab,
    onKeep,
    onDiscard,
}: ConfirmDiscardProps) => (
    <div className="bilda-confirm-discard">
        <Text as="p">
            <Trans>
                “{tab.resource.title}” has unsaved changes. Closing the tab
                discards them — the resource itself stays in the project.
            </Trans>
        </Text>
        <div className="bilda-confirm-discard__actions">
            <Button variant="secondary" onClick={onKeep}>
                <Trans>Keep editing</Trans>
            </Button>
            <Button variant="danger" onClick={onDiscard}>
                <Trans>Discard changes</Trans>
            </Button>
        </div>
    </div>
);
